import { useRef, useState } from 'react'
import { uploadProfileImage } from '@/lib/uploadImage'
import type { WizardState } from './useWizardState'

interface Props {
  state: WizardState
  onChange: (partial: Partial<WizardState>) => void
  slugAvailable: boolean | null
  checkingSlug: boolean
}

export function WizardStepProfile({ state, onChange, slugAvailable, checkingSlug }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [showUrlInput, setShowUrlInput] = useState(false)

  function handleNameChange(value: string) {
    const handle = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 30)
    onChange({ display_name: value, handle })
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadError(null)
    setUploading(true)
    try {
      const url = await uploadProfileImage(file)
      onChange({ profile_image_url: url })
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Set up your profile
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        This is how visitors will see you on your bio page.
      </p>

      <div className="space-y-5 max-w-md">
        {/* Display Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Display Name
          </label>
          <input
            type="text"
            value={state.display_name}
            onChange={e => handleNameChange(e.target.value)}
            placeholder="Your Name"
            maxLength={100}
            autoFocus
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent"
          />
        </div>

        {/* Handle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Username
          </label>
          <div className="flex items-center">
            <span className="px-3 py-2.5 bg-gray-100 dark:bg-white/5 border border-r-0 border-gray-300 dark:border-gray-600 rounded-l-lg text-sm text-gray-500 dark:text-gray-400">
              @
            </span>
            <input
              type="text"
              value={state.handle}
              onChange={e => onChange({ handle: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 30) })}
              placeholder="your-handle"
              className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-r-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent"
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              urlzippy.com/@{state.handle || 'your-handle'}
            </span>
            <span className="text-xs">
              {checkingSlug && <span className="text-gray-400">Checking...</span>}
              {!checkingSlug && slugAvailable === true && state.handle.length >= 3 && (
                <span className="text-green-500 flex items-center gap-0.5">
                  <span className="material-symbols-outlined text-[14px]">check_circle</span>
                  Available
                </span>
              )}
              {!checkingSlug && slugAvailable === false && (
                <span className="text-red-500 flex items-center gap-0.5">
                  <span className="material-symbols-outlined text-[14px]">cancel</span>
                  Taken
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bio
          </label>
          <textarea
            value={state.bio}
            onChange={e => onChange({ bio: e.target.value.slice(0, 80) })}
            placeholder="A short description about you"
            rows={2}
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent resize-none"
          />
          <p className="text-xs text-gray-400 text-right mt-0.5">
            {state.bio.length}/80
          </p>
        </div>

        {/* Profile Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Profile Picture
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-[#2B6CEE] dark:hover:border-[#2B6CEE] transition-colors group flex-shrink-0"
            >
              {uploading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-white/10">
                  <span className="material-symbols-outlined text-gray-400 animate-spin text-[20px]">progress_activity</span>
                </div>
              ) : state.profile_image_url ? (
                <>
                  <img
                    src={state.profile_image_url}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="material-symbols-outlined text-white text-[20px]">edit</span>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 dark:bg-white/5 group-hover:bg-gray-100 dark:group-hover:bg-white/10 transition-colors">
                  <span className="material-symbols-outlined text-gray-400 text-[24px]">add_a_photo</span>
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {uploading ? 'Uploading...' : 'Click to upload a photo'}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                JPG, PNG, GIF, or WebP. Max 2MB.
              </p>
              <button
                type="button"
                onClick={() => setShowUrlInput(!showUrlInput)}
                className="text-xs text-[#2B6CEE] hover:underline mt-1"
              >
                {showUrlInput ? 'Hide URL input' : 'Or paste image URL'}
              </button>
            </div>
          </div>

          {showUrlInput && (
            <input
              type="text"
              value={state.profile_image_url}
              onChange={e => onChange({ profile_image_url: e.target.value })}
              placeholder="https://example.com/photo.jpg"
              className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent text-sm"
            />
          )}

          {uploadError && (
            <p className="mt-1.5 text-xs text-red-500">{uploadError}</p>
          )}
        </div>
      </div>
    </div>
  )
}
